// API
import api from '../../api'

// Constants
import { AnalyticsConstants, AppointmentTypeEnum, AppraisalStatuses, CustLoanTypeSelection, KeyConstants, RegexConstants } from '../../constants'
import LangFiles from '../../locales/en.json'

// Libraries
import dayjs from 'dayjs'
import router from '../../router'
import timezone from 'dayjs/plugin/timezone'

// Utilities
import Analytics from '../../utility/analyticsUtil'
import AppBrowser from '../../utility/appBrowserUtil'
import Environment from '../../utility/environmentUtils'
import Formatter from '../../utility/formatterUtil'
import isEmpty from '../../utility/isEmpty'
import Logger from '../../utility/loggerUtil'
import Session from '../../utility/sessionUtil'
import SessionRecord from '../../utility/sessionRecordUtil'
import Wait from '../../utility/waitUtil'
import { getDefaultState } from '../../utility/applicationUtil'

dayjs.extend(timezone)

export default {
  async createApplication (context) {
    try {
      if (context.rootGetters['applicationSession/isCcfiBrand'] && !context.rootGetters['applicationSession/isSkipCcfiFlow']) {
        context.commit('updateCustomerInfo', { ccfiStoreNumber: context.state.ccfiStoreNumber || Session.getCookie(KeyConstants.cookies.ccfiStoreNumberRedirect) })

        if (isEmpty(context.state.ccfiStoreNumber)) {
          throw Error('Missing CCFI Store Number')
        }
      }

      let mobileMoneyLocation
      let mobileMoneyType

      if (context.rootState.applicationSession.config.enableMobileMoney) {
        mobileMoneyLocation = context.rootState.applicationRetailStore.mobileMoneyLocation
        mobileMoneyType = context.rootState.applicationRetailStore.mobileMoneyType
      }

      const response = await api.application.create(
        context.rootState.applicationRetailStore.number,
        context.state.firstName,
        context.state.middleName,
        context.state.lastName,
        context.state.suffixName,
        context.state.cellPhoneNumber,
        context.state.email,
        context.state.state,
        context.state.zip,
        'Title',
        context.state.leadId,
        context.state.trafficChannel,
        context.state.invitationCode,
        context.state.language,
        context.rootState.applicationVehicle.year,
        context.rootState.applicationVehicle.make,
        context.rootState.applicationVehicle.model,
        context.rootState.applicationVehicle.series,
        context.rootState.applicationVehicle.style,
        context.state.dateOfBirth,
        context.state.tin,
        context.state.noTin,
        context.state.alienRegistrationNumber,
        context.rootState.applicationSession.showOwnershipScreen ? context.state.hasTitle : true,
        context.state.hasTitleWithLender,
        context.state.referralCode,
        context.state.address,
        context.state.city,
        context.state.weight,
        context.state.height,
        context.state.sex,
        context.state.idSelection,
        context.state.idState,
        context.state.idNumber,
        context.state.idExpiration,
        context.state.scanIdBarcodeData,
        context.state.ccfiStoreNumber,
        mobileMoneyLocation,
        mobileMoneyType,
        context.rootState.applicationSession.applicationOptions.skipCcfiFlow
      )

      await context.dispatch('cancelLead')

      sessionStorage.removeItem('customerData')
      sessionStorage.removeItem('vehicleData')

      if (!isEmpty(context.state.email)) {
        Analytics.setUserProperty(AnalyticsConstants.userProperties.email, context.state.email)
      }

      context.commit('updateCustomerInfo', {
        applicationId: response.applicationId,
        customerId: response.customerId,
        selectedRetailStore: context.rootState.applicationRetailStore.number?.toString(),
        newCustomerForAnalytics: response.newCustomerForAnalytics
      })

      await context.dispatch('saveLoanProduct', {
        applicationId: context.state.applicationId,
        loanProduct: context.state.loanProduct,
        custLoanTypeSelection: context.state.custLoanTypeSelection
      })

      if (!isEmpty(context.getters.customerTypeAnalytics)) {
        Analytics.logEvent(AnalyticsConstants.eventName.selectItem, { item_id: 'customer_type', value: context.getters.customerTypeAnalytics, mdm_id: `${context.rootState.customer.mdmId !== undefined ? context.rootState.customer.mdmId : ''}` })
        Analytics.setUserProperty(AnalyticsConstants.userProperties.customerType, context.getters.customerTypeAnalytics)
      }

      Analytics.logEvent(AnalyticsConstants.eventName.tmxExpressApplication, {
        status: AnalyticsConstants.attributeValues.status.selected,
        value: AnalyticsConstants.attributeValues.status.selected,
        expiration_date: `${dayjs().unix()}`,
        email: context.state.email,
        state: context.state.state,
        productType: context.getters.analyticProductType
      }, { logIterableEvent: true })

      await context.dispatch('saveLeadEvent', {
        eventName: KeyConstants.leadEvents.events.applicationStarted,
        status: KeyConstants.leadEvents.status.new
      })

      SessionRecord.setUserIdentifier(response.applicationId, AnalyticsConstants.userProperties.applicationId)

      await api.user.refreshToken(true).catch(e => { // We need a new Cognito ID token after a cognito attribute is updated such as the lms_customer_ids attribute
        Logger.error('Error refreshing after creating application')
        Logger.debug(e)
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/createApplication')
      Logger.debug(error)
      throw error
    }
  },

  async getCustomerApplication (context, data) {
    try {
      if (context.rootGetters['applicationSession/isCcfiBrand']) {
        context.commit('updateCustomerInfo', { ccfiStoreNumber: context.state.ccfiStoreNumber || Session.getCookie(KeyConstants.cookies.ccfiStoreNumberRedirect) })
      }

      let mobileMoneyLocation
      let mobileMoneyType

      if (context.rootState.applicationSession.config.enableMobileMoney) {
        mobileMoneyLocation = context.rootState.applicationRetailStore.mobileMoneyLocation
        mobileMoneyType = context.rootState.applicationRetailStore.mobileMoneyType
      }

      const { applicationId, leadId } = data || {}
      const response = await api.application.getCustomerApplication(applicationId, context.state.ccfiStoreNumber, mobileMoneyLocation, mobileMoneyType, context.rootState.applicationRetailStore.number, context.rootState.applicationSession.applicationOptions.skipCcfiFlow)

      if (!response.success && response.thereAreActiveLoans) {
        return
      }

      await context.dispatch('cancelLead')

      if ((context.state.custLoanTypeSelection === CustLoanTypeSelection.titleLoan || context.state.custLoanTypeSelection === CustLoanTypeSelection.signatureLoan)) {
        await context.dispatch('saveLoanProduct', {
          applicationId: response.applicationId,
          loanProduct: context.state.loanProduct,
          custLoanTypeSelection: context.state.custLoanTypeSelection
        })
        response.custLoanTypeSelection = context.state.custLoanTypeSelection
      }

      context.commit('updateCustomerInfo', {
        firstName: response.firstName,
        middleName: response.middleName,
        lastName: response.lastName,
        suffixName: response.suffixName,
        cellPhoneNumber: response.cellPhoneNumber,
        email: response.email,
        address: response.address,
        optionalAddress: response.optionalAddress,
        city: response.city,
        zip: response.zip,
        state: response.state,
        mailingAddress: response.mailingAddress,
        mailingOptionalAddress: response.mailingOptionalAddress,
        mailingCity: response.mailingCity,
        mailingZip: response.mailingZip,
        mailingState: response.mailingState,
        addressSameAsMailingAddress: response.addressSameAsMailingAddress,
        dateOfBirth: response.dateOfBirth,
        isCustomerInBankruptcy: response.isCustomerInBankruptcy,
        activeDuty: response.activeDuty,
        userName: response.userName,
        maritalStatus: response.maritalStatus,
        idSelection: response.idSelection,
        idNumber: response.idNumber,
        idState: response.idState,
        idExpiration: response.idExpiration === '01/01/1901' ? undefined : response.idExpiration,
        race: response.race,
        weight: response.weight,
        height: response.height,
        sex: response.sex,
        tin: response.tin,
        alienRegistrationNumber: response.alienRegistration,
        selectedRetailStore: response.storeNumber?.toString(),
        applicationId: response.applicationId,
        applicationStatus: response.applicationStatus,
        appraisalId: response.appraisalId,
        appraisalStatus: response.appraisalStatus,
        requestedAmount: response.requestedAmount,
        optIn: response.optIn,
        newCustomer: response.newCustomer,
        noTin: response.noTin,
        hasTitle: response.hasTitle,
        hasTitleWithLender: response.hasTitleWithLender,
        allowSelfAppraisal: response.allowSelfAppraisal,
        employmentPerWeek: response.employmentPerWeek,
        employmentLength: response.employmentLength,
        loanProduct: response.loanProduct,
        bankName: response.bankName,
        routingNum: response.routingNum,
        accountNum: response.accountNum,
        returnCustIndicator: response.returnCustIndicator,
        custLoanTypeSelection: response.custLoanTypeSelection,
        existingFlexLoan: response.existingFlexLoan,
        leadId: response.leadId || leadId
      })

      context.commit('applicationVehicle/updateVehicleInformation', {
        year: response.year,
        make: response.make,
        model: response.model,
        series: response.series,
        style: response.style,
        description: response.description,
        vehicleMileage: response.vehicleMileage,
        customerEstimate: response.customerEstimate,
        lienHolderName: response.lienHolderName,
        lienHolderPhone: response.lienHolderPhone,
        lienHolderAccountNumber: response.lienHolderAccountNumber,
        estimatedPayoffAmount: response.estimatedPayoffAmount,
        registrationCounty: response.registrationCounty
      }, { root: true })

      context.commit('applicationIncome/updateIncomeList', {
        incomeList: response.incomeList
      }, { root: true })

      context.commit('applicationMarital/updateMaritalData', {
        isMarried: response.isMarried,
        spousesName: response.spousesName,
        spousesAddress: response.spousesAddress
      }, { root: true })

      context.commit('applicationRetailStore/setStore', {
        // If there's Mobile Money data currently in the store (from Redirect) override incoming data
        number: response.storeNumber?.toString(),
        mobileMoneyLocation: context.rootState.applicationRetailStore.mobileMoneyLocation || response.mobileMoneyLocation?.toString(),
        mobileMoneyType: context.rootState.applicationRetailStore.mobileMoneyType || response.mobileMoneyType?.toString()
      }, { root: true })

      if (response.newCustomer || (!response.newCustomer && !response.newApplicationCreated)) {
        context.commit('applicationIncome/updateExpenses', {
          rent: response.rent,
          utilities: response.utilities,
          insurance: response.insurance,
          otherExpenses: response.otherExpenses,
          signatureInitials: undefined
        }, { root: true })
      }

      if (response.newApplicationCreated) {
        Analytics.logEvent(AnalyticsConstants.eventName.tmxExpressApplication, {
          status: AnalyticsConstants.attributeValues.status.selected,
          value: AnalyticsConstants.attributeValues.status.selected,
          expiration_date: `${dayjs().unix()}`,
          email: context.state.email,
          state: context.state.state,
          productType: context.getters.analyticProductType
        }, { logIterableEvent: true })

        await context.dispatch('saveLeadEvent', {
          eventName: KeyConstants.leadEvents.events.applicationStarted,
          status: KeyConstants.leadEvents.status.returning
        })
      }

      if (!isEmpty(context.getters.customerTypeAnalytics)) {
        Analytics.logEvent(AnalyticsConstants.eventName.selectItem, { item_id: 'customer_type', value: context.getters.customerTypeAnalytics, mdm_id: `${context.rootState.customer.mdmId !== undefined ? context.rootState.customer.mdmId : ''}` })
        Analytics.setUserProperty(AnalyticsConstants.userProperties.customerType, context.getters.customerTypeAnalytics)
      }

      SessionRecord.setUserIdentifier(response.applicationId, AnalyticsConstants.userProperties.applicationId)

      return response
    } catch (error) {
      Logger.error('Error on applicationCustomer/getCustomerApplications')
      Logger.debug(error)
      throw error
    }
  },

  async updateApplication (context, personalData) {
    try {
      let response
      if (!isEmpty(context.state.applicationId)) {
        response = await api.application.update(
          personalData.firstName,
          personalData.middleName,
          personalData.lastName,
          personalData.suffixName,
          context.state.applicationId,
          personalData.cellPhoneNumber,
          personalData.email
        )
      }

      context.commit('updateCustomerInfo', {
        firstName: personalData.firstName,
        middleName: personalData.middleName,
        lastName: personalData.lastName,
        suffixName: personalData.suffixName,
        cellPhoneNumber: personalData.cellPhoneNumber,
        email: personalData.email,
        applicationId: response?.applicationId
      })

      if (!isEmpty(personalData.email)) {
        Analytics.setUserProperty(AnalyticsConstants.userProperties.email, personalData.email)
      }
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateApplication')
      Logger.debug(error)
      throw error
    }
  },

  async updateApplicationWithScanId (context, personalData) {
    try {
      const address = personalData.address || ''
      const strippedAddress = address.replace(new RegExp(RegexConstants.addressSpecialCharacters, 'g'), '')

      let response
      if (!isEmpty(context.state.applicationId)) {
        response = await api.application.update(
          personalData.firstName,
          personalData.middleName,
          personalData.lastName,
          personalData.suffixName,
          context.state.applicationId,
          undefined,
          undefined,
          personalData.dateOfBirth,
          strippedAddress,
          personalData.zip,
          personalData.city,
          personalData.state,
          personalData.weight,
          personalData.height,
          personalData.sex,
          personalData.idSelection,
          personalData.idState,
          personalData.idNumber,
          personalData.idExpiration,
          personalData.scanIdBarcodeData
        )
      }

      context.commit('updateCustomerScanData', {
        firstName: personalData.firstName,
        middleName: personalData.middleName,
        lastName: personalData.lastName,
        suffixName: personalData.suffixName,
        dateOfBirth: personalData.dateOfBirth,
        address: strippedAddress,
        zip: personalData.zip,
        city: personalData.city,
        state: personalData.state,
        weight: personalData.weight,
        height: personalData.height,
        sex: personalData.sex,
        idSelection: personalData.idSelection,
        idState: personalData.idState,
        idNumber: personalData.idNumber,
        idExpiration: personalData.idExpiration,
        applicationId: response?.applicationId,
        scanIdBarcodeData: personalData.scanIdBarcodeData
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateApplicationWithScanId')
      Logger.debug(error)
      throw error
    }
  },

  async updateAddress (context, addressInfo) {
    try {
      let updatedAddress = {}

      if (addressInfo.addressType === 'home') {
        const address = addressInfo.address || context.state.address
        const strippedAddress = address.replace(new RegExp(RegexConstants.addressSpecialCharacters, 'g'), '')

        const optionalAddress = addressInfo.optionalAddress || ''
        const strippedOptionalAddress = optionalAddress.replace(new RegExp(RegexConstants.addressSpecialCharacters, 'g'), '')

        updatedAddress = {
          address: strippedAddress,
          optionalAddress: strippedOptionalAddress,
          city: addressInfo.city,
          zip: addressInfo.zip,
          state: addressInfo.state,
          addressSameAsMailingAddress: addressInfo.addressSameAsMailingAddress,
          mailingAddress: undefined,
          mailingOptionalAddress: undefined,
          mailingCity: undefined,
          mailingZip: undefined,
          mailingState: undefined
        }

        if (addressInfo.addressSameAsMailingAddress) {
          updatedAddress.mailingAddress = strippedAddress
          updatedAddress.mailingOptionalAddress = strippedOptionalAddress
          updatedAddress.mailingCity = addressInfo.city
          updatedAddress.mailingZip = addressInfo.zip
          updatedAddress.mailingState = addressInfo.state
        }

        await api.application.updateAddress(
          context.state.applicationId,
          addressInfo.addressSameAsMailingAddress || context.state.addressSameAsMailingAddress,
          updatedAddress.address,
          updatedAddress.optionalAddress,
          updatedAddress.city || context.state.city,
          updatedAddress.zip || context.state.zip,
          updatedAddress.state || context.state.state,
          updatedAddress.mailingAddress,
          updatedAddress.mailingOptionalAddress,
          updatedAddress.mailingCity,
          updatedAddress.mailingZip,
          updatedAddress.mailingState
        )

        if (addressInfo.state !== context.state.state) {
          context.commit('applicationSession/setLastDocumentViewed', { lastDocumentViewed: undefined }, { root: true }) // Clear any previously saved disclosure view state
        }
      } else if (addressInfo.addressType === 'mailing') {
        const mailingAddress = addressInfo.mailingAddress || context.state.mailingAddress || ''
        const strippedMailingAddress = mailingAddress.replace(new RegExp(RegexConstants.addressSpecialCharacters, 'g'), '')

        const mailingOptionalAddress = addressInfo.mailingOptionalAddress || ''
        const strippedMailingOptionalAddress = mailingOptionalAddress.replace(new RegExp(RegexConstants.addressSpecialCharacters, 'g'), '')

        updatedAddress = {
          mailingAddress: strippedMailingAddress,
          mailingOptionalAddress: strippedMailingOptionalAddress,
          mailingCity: addressInfo.mailingCity,
          mailingZip: addressInfo.mailingZip,
          mailingState: addressInfo.mailingState,
          addressSameAsMailingAddress: addressInfo.addressSameAsMailingAddress
        }

        await api.application.updateAddress(
          context.state.applicationId,
          addressInfo.addressSameAsMailingAddress || context.state.addressSameAsMailingAddress,
          updatedAddress.address,
          updatedAddress.optionalAddress,
          updatedAddress.city,
          updatedAddress.zip,
          updatedAddress.state,
          updatedAddress.mailingAddress,
          updatedAddress.mailingOptionalAddress,
          updatedAddress.mailingCity || context.state.mailingCity,
          updatedAddress.mailingZip || context.state.mailingZip,
          updatedAddress.mailingState || context.state.mailingState
        )
      } else { // Save existing info to fill in longitude and latitude
        await api.application.updateAddress(
          context.state.applicationId,
          context.state.addressSameAsMailingAddress,
          context.state.address,
          context.state.optionalAddress,
          context.state.city,
          context.state.zip,
          context.state.state,
          context.state.mailingAddress,
          context.state.mailingOptionalAddress,
          context.state.mailingCity,
          context.state.mailingZip,
          context.state.mailingState
        )
      }

      let stateIsNotSupported = false
      if (addressInfo.state && context.state.state !== addressInfo.state) {
        // Set default store if state isn't supported
        stateIsNotSupported = isEmpty(await context.dispatch('applicationSession/getProductConfiguration', { state: addressInfo.state, productType: context.getters.loanProduct }, { root: true }).catch(error => {
          Logger.error('Error trying to get product configuration')
          Logger.debug(error)
        }))
      }

      context.commit('updateCustomerInfo', {
        address: updatedAddress.address,
        optionalAddress: updatedAddress.optionalAddress,
        city: addressInfo.city,
        zip: addressInfo.zip,
        state: addressInfo.state,
        mailingAddress: updatedAddress.mailingAddress,
        mailingOptionalAddress: updatedAddress.mailingOptionalAddress,
        mailingCity: addressInfo.mailingCity,
        mailingZip: addressInfo.mailingZip,
        mailingState: addressInfo.mailingState,
        addressSameAsMailingAddress: addressInfo.addressSameAsMailingAddress
      })

      return stateIsNotSupported
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateAddress')
      Logger.debug(error)
      throw error
    }
  },

  async updateIdentification (context, personalData) {
    try {
      await api.application.updateIdentification(
        personalData.idSelection,
        personalData.idNumber,
        personalData.idState,
        dayjs(personalData.idExpiration, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        context.state.applicationId
      )

      context.commit('updateCustomerInfo', {
        idSelection: personalData.idSelection,
        idNumber: personalData.idNumber,
        idState: personalData.idState,
        idExpiration: personalData.idExpiration
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateIdentification')
      Logger.debug(error)
      throw error
    }
  },

  async updateBankingInfo (context, personalData) {
    try {
      await api.application.updateBankingInfo(
        personalData.bankName,
        personalData.routingNum,
        personalData.accountNum,
        context.state.applicationId
      )

      context.commit('updateCustomerInfo', {
        routingNum: personalData.routingNum,
        accountNum: personalData.accountNum,
        confirmAccountNum: personalData.confirmAccountNum,
        tosAccepted: personalData.tosAccepted,
        bankAccountEntryStatus: personalData.bankAccountEntryStatus,
        bankName: personalData.bankName
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateBankingInfo')
      Logger.debug(error)
      throw error
    }
  },

  async updateReturnCustIndicator (context, personalData) {
    try {
      await api.application.updateReturnCustIndicator(personalData.returnCustIndicator, context.state.applicationId)

      context.commit('updateCustomerInfo', {
        returnCustIndicator: personalData.returnCustIndicator
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateReturnCustIndicator')
      Logger.debug(error)
      throw error
    }
  },

  async updateDateOfBirth (context, personalData) {
    try {
      if (!isEmpty(context.state.applicationId)) {
        await api.application.updateDateOfBirth(Formatter.convertDateToZulu(personalData.dateOfBirth), context.state.applicationId)
      }

      context.commit('updateCustomerInfo', {
        dateOfBirth: personalData.dateOfBirth
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateDateOfBirth')
      Logger.debug(error)
      throw error
    }
  },

  async updateTin (context, personalData) {
    try {
      if (!isEmpty(context.state.applicationId)) {
        await api.application.updateTin(
          personalData.tin,
          personalData.noTin,
          personalData.alienRegistrationNumber,
          context.state.applicationId
        )
      }

      context.commit('updateCustomerInfo', {
        tin: personalData.tin,
        noTin: personalData.noTin,
        alienRegistrationNumber: personalData.alienRegistrationNumber
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateTin')
      Logger.debug(error)
      throw error
    }
  },

  async updateTitle (context, personalData) {
    try {
      await api.application.updateTitle(
        personalData.hasTitle,
        personalData.hasTitleWithLender,
        context.state.applicationId
      )
      context.commit('updateCustomerInfo', {
        hasTitle: personalData.hasTitle,
        hasTitleWithLender: personalData.hasTitleWithLender
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateTitle')
      Logger.debug(error)
      throw error
    }
  },

  async updateDemographics (context, personalData) {
    try {
      await api.application.updateDemographics(
        personalData.race,
        personalData.weight,
        personalData.height,
        personalData.sex,
        context.state.applicationId
      )

      context.commit('updateCustomerInfo', {
        race: personalData.race,
        weight: personalData.weight,
        height: personalData.height,
        sex: personalData.sex
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateDemographics')
      Logger.debug(error)
      throw error
    }
  },

  async updateRequestedAmount (context, personalData) {
    try {
      await api.application.updateRequestedAmount(personalData.requestedAmount, context.state.applicationId)

      context.commit('updateCustomerInfo', {
        requestedAmount: personalData.requestedAmount
      })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateRequestedAmount')
      Logger.debug(error)
      throw error
    }
  },

  async getApprovedAmount (context) {
    try {
      const attachedId = context.getters.isSignatureLoanProduct ? context.state.applicationId : context.state.appraisalId
      const productType = context.getters.loanProduct
      Logger.info(`attachId = ${attachedId} productType = ${productType}`)
      const response = await api.applicationLoan.getApprovalAmount(attachedId, productType)

      context.commit('updateCustomerInfo', {
        approvedAmount: response.approvedAmount
      })

      return response.approvedAmount
    } catch (error) {
      Logger.error('Error on applicationCustomer/getApprovedAmount')
      Logger.debug(error)
      throw error
    }
  },

  async verifyIdentity (context, data) {
    try {
      const response = await api.applicationCustomer.verifyIdentity(data.applicationId)
      const verification = response.approved
      context.commit('updateCustomerInfo', { identityVerified: verification })
    } catch (error) {
      Logger.error('Error on applicationCustomer/verifyIdentity')
      Logger.debug(error)
      throw error
    }
  },

  async verifyMlv (context, data) {
    try {
      const response = await api.applicationCustomer.verifyMlv(data.applicationId, data.productCode)
      const verification = response.approved
      context.commit('updateCustomerInfo', { mlvVerified: verification })
    } catch (error) {
      Logger.error('Error on applicationCustomer/verifyMlv')
      Logger.debug(error)
      throw error
    }
  },

  async applicationStatusUpdate (context, data) {
    try {
      await api.applicationCustomer.applicationStatusUpdate(data.applicationId)
      context.commit('updateCustomerInfo', { applicationSubmitted: true })

      Session.deleteCookie(KeyConstants.cookies.qrScanStoreNumberRedirect)
    } catch (error) {
      Logger.error('Error on applicationCustomer/applicationStatusUpdate')
      Logger.debug(error) // Ignoring as 'applicationSubmitted' will signify success or not
    }
  },

  async applicationDeclineUpdate (context) {
    try {
      await api.applicationCustomer.applicationDeclineUpdate(context.state.applicationId)
    } catch (error) {
      Logger.error('Error on setting application declined')
      Logger.debug(error)
      throw error
    }

    try {
      Analytics.logEvent(AnalyticsConstants.eventName.tmxExpressApplication, {
        status: AnalyticsConstants.attributeValues.status.declined,
        reason: 'application(express site)',
        value: AnalyticsConstants.attributeValues.status.declined,
        state: context.state.state || '',
        productType: context.getters.analyticProductType
      }, { logIterableEvent: true })

      await context.dispatch('saveLeadEvent', {
        eventName: KeyConstants.leadEvents.events.submitted,
        status: KeyConstants.leadEvents.status.declined,
        storeNumber: this.storeNumber
      })
    } catch (error) {
      Logger.error('Error on saving application declined analytics')
      Logger.debug(error)
    }
  },

  async updateCommunication (context, data) {
    try {
      const response = await api.applicationTextVerification.updateCommunication(
        data.applicationId,
        data.optIn,
        data.resendCode,
        data.cellPhoneNumber,
        data.skipVerification)

      context.commit('updatePhoneVerified', {
        phoneNumberVerified: response.verified,
        optIn: data.optIn
      })
    } catch (error) {
      context.commit('updatePhoneVerified', {
        phoneNumberVerified: false,
        optIn: data.optIn
      })
      Logger.error('Error on applicationCustomer/updateCommunication')
      Logger.debug(error)
      throw error
    }
  },

  async confirmVerificationCode (context, data) {
    try {
      const response = await api.applicationTextVerification.confirmVerificationCode(
        data.verificationNumber,
        data.applicationId,
        data.optIn)

      const verification = response.verified
      context.commit('updatePhoneVerified', { phoneNumberVerified: verification })
    } catch (error) {
      Logger.error('Error on applicationCustomer/confirmVerificationCode')
      Logger.debug(error)
      throw error
    }
  },

  async submitDisclosure (context, data) {
    await api.applicationCustomer.submitDisclosure(
      context.state.applicationId,
      context.rootState.applicationSession.disclosuresLinksList,
      getSubmitRequest(context, data.disclosure))

    context.commit('updateCustomerInfo', {
      signatureName: data.disclosure.signatureName
    })
  },

  async getCustomerApplicationNextPage (context) {
    if (context.state.newCustomer && ((!context.state.appraisalStatus && !context.state.applicationStatus) || context.state.applicationStatus === 'start' || context.state.applicationStatus === 'inprog')) {
      return context.getters.returnUser ? 'BorrowAmount' : 'PersonalData'
    }

    if (!context.state.appraisalStatus && context.state.applicationStatus !== 'submit') {
      return 'BorrowAmount'
    }

    if (context.state.appraisalStatus && [AppraisalStatuses.declined, AppraisalStatuses.withdraw].includes(context.state.appraisalStatus)) {
      return !context.state.newCustomer ? 'BorrowAmount' : context.getters.returnUser ? 'PersonalInfoSummary' : 'PersonalData'
    }

    if (context.state.appraisalStatus && ![AppraisalStatuses.complete].includes(context.state.appraisalStatus)) {
      return 'LoanReview'
    }

    const isCcf = context.rootGetters['applicationSession/isCcfiBrand'] && !context.rootGetters['applicationSession/isSkipCcfiFlow']

    let appointments = []
    if (!isCcf) {
      appointments = await context.dispatch('applicationAppointment/getAppointment', { storeNumber: context.state.selectedRetailStore, timezone: dayjs.tz.guess(), appointmentType: AppointmentTypeEnum.approval }, { root: true }) || []
    }

    if (!context.state.appraisalStatus && context.state.applicationStatus === 'submit') {
      return appointments.length !== 0 ? 'ScheduleConfirm' : context.getters.postSubmissionPage()
    }

    if (context.state.appraisalStatus === AppraisalStatuses.complete) {
      if (isCcf) { // Always send to LoanReview for approved CCF appraisals
        return 'LoanReview'
      }

      return appointments.length !== 0 ? 'ScheduleConfirm' : 'LoanApproval'
    }

    // Final application status is for appraisal statuses start and inprog
    return context.getters.postSubmissionPage()
  },

  async updatePostRegisterAndNavigate (context) {
    try {
      const address = context.state.address
      const city = context.state.city
      const productType = context.getters.loanProduct
      const state = context.state.state || getDefaultState()
      const zip = context.state.zip
      const isMobileMoney = context.rootGetters['applicationSession/isMobileMoney']
      const setStore = context.rootGetters['applicationSession/setStore']

      const stateIsSupported = !isEmpty(await context.dispatch('applicationSession/getProductConfiguration', {
        state,
        productType
      }, { root: true }).catch(error => {
        Logger.error('Error trying to get product configuration')
        Logger.debug(error)
      }))

      if (!isEmpty(zip) && !isMobileMoney && stateIsSupported && setStore) {
        await context.dispatch('applicationRetailStore/setClosestStore', {
          address,
          city,
          state,
          zip
        }, { root: true }).catch(e => {
          Logger.error('Error updating retail store')
          Logger.debug(e)
        })
      }

      if (isEmpty(context.state.firstName) || isEmpty(context.state.lastName) || isEmpty(context.state.dateOfBirth)) {
        router.replace({ name: 'PersonalData' })
        return
      }

      await context.dispatch('createApplication')
      context.dispatch('applicationFieldTracking/updateFieldTracking', {}, { root: true }) // Not awaiting

      router.replace({ name: 'Communication' })
    } catch (e) {
      Logger.error('An error occurred while confirming application account verification code')
      Logger.debug(e)

      if (e.message === 'Missing CCFI Store Number') {
        router.push({ name: 'OopsPage', params: { ccfiStoreNumberIsMissing: true } })
        return
      }

      const responseError = e?.response?.data?.error

      if (responseError === 'SbtSubscribeFailed') {
        context.commit('session/setVerified', { verified: false }, { root: true })
        router.replace({ name: 'Communication' })
      } else {
        throw e
      }
    }
  },

  async getReturnCustomerApplicationAndNavigate (context) {
    try {
      Wait.waitStart('getReturnCustomerApplicationAndNavigate')
      const customerApplication = await context.dispatch('getCustomerApplication').catch(error => {
        if (error?.response?.data?.error === 'Missing vergentCustomerId') {
          if (!isEmpty(context.state.email) && error.response.data.cause === 'hasReferralBalance') {
            return { name: 'ReferralRewardsHome', params: { error: LangFiles.global.referral_rewards_balance_error } }
          }

          return { pageName: 'PersonalData' }
        }

        Logger.error('Error getting customer application details')
        Logger.debug(error)

        throw error
      })

      try {
        await Analytics.updateIdentity()
        SessionRecord.updateIdentity()
      } catch (error) {
        Logger.error('Error updating analytics identity in getReturnCustomerApplicationAndNavigate')
        Logger.debug(error) // Ignoring any errors
      }

      if (!customerApplication) {
        return { name: 'LoanActive' }
      }

      const defaultState = getDefaultState()

      await context.dispatch('applicationSession/getProductConfiguration', { state: context.state.state ? context.state.state : defaultState, productType: context.getters.loanProduct }, { root: true }).catch(e => {
        Logger.error('Error getting product config.')
        Logger.debug(e)
      })

      if (!isEmpty(customerApplication.name)) { // If missing vergent customer ID no app created so send to personal data or refer rewards for NCR users with cash out balance
        return customerApplication
      }

      const page = await context.dispatch('getCustomerApplicationNextPage')

      if (!page || (page !== 'PersonalData' && page !== 'BorrowAmount')) {
        const pageName = !page ? 'LoanActive' : page
        const params = page === 'LoanDenial' ? { reason: AnalyticsConstants.attributeValues.reason.unsupportedState } : undefined
        return { name: pageName, params }
      }

      const customerData = buildCustomerData(context)

      if (customerData && context.state.applicationId) {
        await context.dispatch('updateLoginData', customerData)
        context.dispatch('applicationFieldTracking/setDefaultFieldTrackingReturnUser', customerApplication, { root: true })
      }

      return { name: page }
    } catch (error) {
      Logger.error('Error getting return application and nav')
      Logger.debug(error)
      throw error
    } finally {
      Wait.waitEnd('getReturnCustomerApplicationAndNavigate')
    }
  },

  async getAppraiserAppLink (context, payload) {
    Wait.waitStart('sendSelfAssessmentText')
    const isOnIos = context.rootGetters['display/isIOS']
    const sendSms = !context.rootState.display.isMobileBrowser // will not send text message for the new web app on mobile until text verbiage is updated

    try {
      if (payload.returnLink) {
        const appUrl = await api.applicationCustomer.sendSelfAssessmentText(context.state.cellPhoneNumber, context.state.applicationId, false)
        return appUrl.assessmentLink
      }

      // Open app via URL if on mobile device, otherwise send link via SMS.
      if (!context.rootState.display.isMobileBrowser) {
        await api.applicationCustomer.sendSelfAssessmentText(context.state.cellPhoneNumber, context.state.applicationId, true)
        Analytics.logEvent(AnalyticsConstants.eventName.selectItem, { item_id: 'download_appraisal' })
        Analytics.logEvent(AnalyticsConstants.eventName.contact, { item_id: 'contact_self_appraisal_link', method: AnalyticsConstants.attributeValues.contactMethods.text })

        return true
      }

      const appUrl = await api.applicationCustomer.sendSelfAssessmentText(context.state.cellPhoneNumber, context.state.applicationId, false)
      Analytics.logEvent(AnalyticsConstants.eventName.selectItem, { item_id: 'download_appraisal' })
      Analytics.logEvent(AnalyticsConstants.eventName.contact, { item_id: 'contact_self_appraisal_link', method: AnalyticsConstants.attributeValues.contactMethods.text })

      isOnIos && context.commit('applicationSession/setForceRefresh', { forceRefreshPage: payload.forceRefreshPage }, { root: true }) // Fix navigation refresh issue on iOS Safari
      context.commit('applicationSession/setSkipRefresh', { skipRefresh: true }, { root: true }) // Skip refresh confirmation before navigating off the site

      if (appUrl.assessmentLink && sendSms) {
        sessionStorage.setItem(KeyConstants.sessionStorage.textSent, 'true')
      }

      window.location = appUrl.assessmentLink // Ensure they navigate from same tab
      return sendSms
    } catch (error) {
      Logger.error('Failed to send assessment text')
      Logger.debug(error)

      throw error
    } finally {
      Wait.waitEnd('sendSelfAssessmentText')
    }
  },

  async updateLoginData (context, data) {
    try {
      const promises = [api.application.updateLoginData(
        data.email,
        data.cellPhoneNumber,
        context.state.applicationId,
        data.referralCode,
        data.hasTitle,
        data.mobileMoneyLocation,
        data.mobileMoneyType
      )]

      if (!isEmpty(data.year) || !isEmpty(data.make) || !isEmpty(data.model) || !isEmpty(data.series) || !isEmpty(data.style)) {
        promises.push(api.applicationVehicleInfo.updateLoginDataVehicle(
          context.state.applicationId,
          data.year,
          data.make,
          data.model,
          data.series,
          data.style
        ))
      }

      await Promise.all(promises)

      context.commit('updateCustomerInfo', { leadId: data.leadId })
    } catch (error) {
      Logger.error('Error on applicationCustomer/updateLoginData')
      Logger.debug(error)
      throw error
    }
  },

  async prescoreApplication (context) {
    Wait.waitStart('Prescore Application')

    try {
      const prescoreResult = await api.applicationCustomer.getPrescore(context.state.applicationId, context.rootState.applicationVehicle.vehicleEstimate || {})
      return prescoreResult
    } catch (error) {
      Logger.error('Failed to get prescore')
      Logger.debug(error)

      throw error
    } finally {
      Wait.waitEnd('Prescore Application')
    }
  },

  async createLead (context) {
    try {
      if (!isEmpty(context.state.leadId) || !isEmpty(context.state.applicationId) || Environment.isCCFBrandEnv()) {
        return
      }

      const { cellPhoneNumber, email, firstName, middleName, lastName, state, zip } = context.state

      const response = await api.applicationLead.createLead(
        firstName,
        middleName,
        lastName,
        cellPhoneNumber,
        email,
        navigator.userAgent,
        zip,
        state
      )

      if (!isEmpty(response?.leadId)) {
        context.commit('updateCustomerInfo', { leadId: response.leadId })
      }
    } catch (error) {
      Logger.error('Failed to create lead')
      Logger.debug(error) // Ignoring error as not required for flow
    }
  },

  async cancelLead (context) {
    try {
      if (isEmpty(context.state.leadId) || isNaN(context.state.leadId)) { // Using NaN protects from old salesforce IDs
        return
      }

      await api.applicationLead.cancelLead(context.state.leadId)
    } catch (error) {
      Logger.error('Failed to cancel lead')
      Logger.debug(error) // Ignoring error as not required for flow
    }
  },

  async saveLeadEvent (context, data) {
    try {
      if (isEmpty(context.state.leadId) || isNaN(context.state.leadId)) { // Using NaN protects from old salesforce IDs
        return
      }

      await api.applicationLead.saveLeadEvent(context.state.leadId, context.state.applicationId, data.eventName, context.getters.fullName, context.state.email, context.state.cellPhoneNumber, data.status, data.ftaApproved, data.appointmentTime, data.storeNumber, data.storeName, data.approvedAmount)
    } catch (error) {
      Logger.error(`Failed to save lead event ${data.eventName}`)
      Logger.debug(error) // Ignoring error as not required for flow
    }
  },

  async saveLoanProduct (context, data) {
    try {
      await api.applicationCustomer.saveLoanProduct(data.applicationId || context.state.applicationId, data.loanProduct, data.custLoanTypeSelection)

      if (data.loanProduct !== context.state.loanProduct) {
        context.commit('applicationSession/setLastDocumentViewed', { lastDocumentViewed: undefined }, { root: true }) // Clear any previously saved disclosure view state
      }

      context.commit('updateCustomerInfo', { loanProduct: data.loanProduct, custLoanTypeSelection: data.custLoanTypeSelection })

      await context.dispatch('applicationSession/getProductConfiguration', { state: context.state.state, productType: data.loanProduct }, { root: true }).catch(error => {
        Logger.error('Error trying to get product configuration')
        Logger.debug(error)
      })
    } catch (error) {
      Logger.error('Failed to save loan product')
      Logger.debug(error)
      throw error
    }
  },

  async getScanIdDataUuid (context, data) {
    return await api.applicationCustomer.getScanIdDataUuid(data.cellPhoneNumber)
  },

  async sendScanIdTextMessage (context, data) {
    return await api.applicationCustomer.sendScanIdTextMessage(data.cellPhoneNumber, data.scanIdDataUuid)
  },

  async getScanIdData (context, data) {
    return await api.applicationCustomer.getScanIdData(data.scanId)
  },

  async saveScanIdData (context, data) {
    return await api.applicationCustomer.saveScanIdData(data.scanId, data.data)
  },

  async getSignatureUrl (context) {
    return await api.applicationCustomer.getSignatureUrl(context.state.applicationId)
  },

  async validateEmail (context, data) {
    try {
      return await api.applicationCustomer.validateEmail(data.email, context.state.applicationId)
    } catch (error) {
      Logger.error('Error on applicationCustomer/validateEmail')
      Logger.debug(error)
      throw error
    }
  },

  async continueApplication (context, inProgressApp) {
    if (!context.rootState.session.expressFlowEnabled) {
      try {
        Analytics.setScreenName('Express Application Site')

        AppBrowser.openInBrowser(inProgressApp ? context.rootState.loans.activeApplication.returnUrl : context.rootState.loans.expressApplicationUrl, '_blank')

        return
      } catch (e) {
        Logger.error('Error opening express application link in browser')
        Logger.debug(e)
        throw e
      }
    }

    try {
      Wait.pushStart()
      return await context.dispatch('getReturnCustomerApplicationAndNavigate')
    } catch (e) {
      Logger.error('Error on applicationCustomer.continueApplication')
      Logger.debug(e)
      Wait.pushEnd()

      throw e
    }
  }
}

const getSubmitRequest = (context, data) => {
  const disclaimerList = []

  context.rootState.applicationSession.disclosuresLinksList.forEach((item) => {
    disclaimerList.push(item.label)
  })

  const optIn = context.state.optIn || {}

  return {
    firstName: context.state.firstName,
    middleName: context.state.middleName,
    lastName: context.state.lastName,
    suffixName: context.state.suffixName,
    userName: context.rootState.session.username,
    requestedAmount: context.state.requestedAmount,
    cellPhoneNumber: context.state.cellPhoneNumber,
    email: context.state.email,
    addressSameAsMailingAddress: context.state.addressSameAsMailingAddress,
    addressLine: context.state.address + (context.state.optionalAddress ? ', ' + context.state.optionalAddress : '') + ', ' + context.state.city + ', ' + context.state.state + ', ' + context.state.zip,
    mailingAddressLine: context.state.mailingAddress + (context.state.mailingOptionalAddress ? ', ' + context.state.mailingOptionalAddress : '') + ', ' + context.state.mailingCity + ', ' + context.state.mailingState + ', ' + context.state.mailingZip,
    dateOfBirth: context.state.dateOfBirth,
    tin: context.state.tin,
    alienRegistrationNumber: context.state.alienRegistrationNumber,
    idSelection: context.state.idSelection,
    idNumber: context.state.idNumber,
    idState: context.state.idState,
    idExpiration: context.state.idExpiration,
    race: data.race,
    weight: data.weight,
    height: data.height,
    sex: data.sex,
    incomeList: context.rootState.applicationIncome.incomeList,
    isCustomerInBankruptcy: context.state.isCustomerInBankruptcy,
    activeDuty: context.state.activeDuty,
    leadId: context.state.leadId,
    trafficChannel: context.state.trafficChannel,
    invitationCode: context.state.invitationCode,
    language: context.state.language,
    hasTitle: context.state.hasTitle,
    hasTitleWithLender: context.state.hasTitleWithLender,
    vehicleDescription:
      context.rootState.applicationVehicle.year + ' ' +
      context.rootState.applicationVehicle.make + ' ' + context.rootState.applicationVehicle.model +
      (isEmpty(context.rootState.applicationVehicle.series) ? '' : ' ' + context.rootState.applicationVehicle.series) +
      (isEmpty(context.rootState.applicationVehicle.style) ? '' : ' ' + context.rootState.applicationVehicle.style),
    vehicleMileage: context.rootState.applicationVehicle.vehicleMileage,
    customerEstimate: context.rootState.applicationVehicle.customerEstimate,
    lienHolderName: isEmpty(context.rootState.applicationVehicle.lienHolderName) ? '' : context.rootState.applicationVehicle.lienHolderName,
    lienHolderPhone: isEmpty(context.rootState.applicationVehicle.lienHolderPhone) ? '' : context.rootState.applicationVehicle.lienHolderPhone,
    lienHolderAccountNumber: isEmpty(context.rootState.applicationVehicle.lienHolderAccountNumber) ? '' : context.rootState.applicationVehicle.lienHolderAccountNumber,
    estimatedPayoffAmount: isEmpty(context.rootState.applicationVehicle.estimatedPayoffAmount) ? 0 : context.rootState.applicationVehicle.estimatedPayoffAmount,
    optInSms: optIn.sms || false,
    smsAcceptedText: data.smsText,
    optInCall: optIn.call || false,
    callAcceptedText: data.callAcceptedText,
    optInEmail: optIn.email || false,
    emailAcceptedText: data.emailAcceptedText.replace('[[customerEmail]]', context.state.email),
    signatureText: data.signatureText,
    signatureName: data.signatureName,
    disclosures: disclaimerList,
    geolocationServicesEnabled: context.rootState.applicationSession.geolocationEnabled,
    geolocationAddress: context.rootState.applicationSession.geolocationAddress,
    maritalStatusRequired: data.maritalStatusRequired,
    isMarried: context.rootState.applicationMarital.isMarried,
    spousesName: context.rootState.applicationMarital.spousesName,
    spousesAddress: context.rootState.applicationMarital.spousesAddress,
    employmentLength: context.state.employmentLength,
    employmentPerWeek: context.state.employmentPerWeek,
    loanProduct: context.getters.loanProduct,
    bankName: context.state.bankName,
    routingNumber: context.state.routingNum,
    accountNumber: context.state.accountNum,
    confirmAccountNumber: context.state.confirmAccountNum,
    customerState: context.state.state,
    existingFlexLoan: context.state.existingFlexLoan,
    expenseRent: context.rootState.applicationIncome.rent,
    expenseUtilities: context.rootState.applicationIncome.utilities,
    expenseInsurance: context.rootState.applicationIncome.insurance,
    expenseOther: context.rootState.applicationIncome.otherExpenses,
    grossMonthlyExpenses: context.rootGetters['applicationIncome/grossMonthlyExpenses'],
    grossMonthlyIncome: context.rootGetters['applicationIncome/grossMonthlyIncome'],
    netMonthlyIncome: context.rootGetters['applicationIncome/netMonthlyIncome'],
    registrationCounty: context.rootState.applicationVehicle.registrationCounty
  }
}

const buildCustomerData = (context, leadId) => {
  // If there's no data to save, do not attempt to save
  if (
    isEmpty(context.state.email) &&
    isEmpty(context.state.cellPhoneNumber) &&
    isEmpty(context.rootState.applicationVehicle.year) &&
    isEmpty(context.rootState.applicationVehicle.make) &&
    isEmpty(context.rootState.applicationVehicle.model) &&
    isEmpty(context.rootState.applicationVehicle.series) &&
    isEmpty(context.rootState.applicationVehicle.style) &&
    (isEmpty(leadId) || isEmpty(context.state.leadId)) &&
    isEmpty(context.state.referralCode) &&
    isEmpty(context.rootState.applicationRetailStore.mobileMoneyLocation) &&
    isEmpty(context.rootState.applicationRetailStore.mobileMoneyType)
  ) {
    return
  }

  return {
    email: context.state.email,
    cellPhoneNumber: context.state.cellPhoneNumber,
    year: context.rootState.applicationVehicle.year,
    make: context.rootState.applicationVehicle.make,
    model: context.rootState.applicationVehicle.model,
    series: context.rootState.applicationVehicle.series,
    style: context.rootState.applicationVehicle.style,
    leadId: isEmpty(context.state.leadId) ? leadId : context.state.leadId,
    referralCode: context.state.referralCode,
    hasTitle: context.rootState.applicationSession.showOwnershipScreen ? context.state.hasTitle : true,
    mobileMoneyLocation: context.rootState.applicationRetailStore.mobileMoneyLocation,
    mobileMoneyType: context.rootState.applicationRetailStore.mobileMoneyType
  }
}
